import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useUser } from "./userContext";
import { useSocket } from "./SocketContext";
import axiosInstance, { API_ENDPOINTS } from "@/config/api";
import AnnouncementModal from "@/components/AnnouncementModal";
import { isAccessTokenUsable } from "@/utils/accessToken";
import { logger } from "@/lib/logger";
import { usePlatformSettings } from "@/context/PlatformSettingsContext";

interface Announcement {
    id: string;
    title: string;
    description: string;
    image_url?: string;
    mobile_image_url?: string;
    layout?: "standard" | "campaign";
    call_to_action_text?: string;
    call_to_action_url?: string;
    priority: number;
    published_at: string;
    expires_at?: string;
    expiresAt?: string;
}

interface AnnouncementContextType {
    pendingAnnouncements: Announcement[];
    fetchPendingAnnouncements: () => Promise<void>;
    showNextAnnouncement: () => void;
    dismissAnnouncement: (id: string, action?: "clicked_cta" | "dismissed") => Promise<void>;
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);
const ANNOUNCEMENT_DISMISSED_STORAGE_KEY = "qit:dismissed-announcements:v1";
const MAX_STORED_DISMISSED_ANNOUNCEMENTS = 200;
const ANNOUNCEMENT_EXPIRY_BUFFER_MS = 60_000;

const readDismissedAnnouncementIds = (): Set<string> => {
    if (typeof window === "undefined") return new Set();
    try {
        const parsed = JSON.parse(localStorage.getItem(ANNOUNCEMENT_DISMISSED_STORAGE_KEY) || "[]");
        if (!Array.isArray(parsed)) return new Set();
        return new Set(parsed.map((id) => String(id || "").trim()).filter(Boolean));
    } catch {
        return new Set();
    }
};

const writeDismissedAnnouncementIds = (ids: Set<string>) => {
    if (typeof window === "undefined") return;
    try {
        const values = Array.from(ids).filter(Boolean).slice(-MAX_STORED_DISMISSED_ANNOUNCEMENTS);
        localStorage.setItem(ANNOUNCEMENT_DISMISSED_STORAGE_KEY, JSON.stringify(values));
    } catch {
        // Dismissal is still kept in memory if localStorage is unavailable.
    }
};

const getAnnouncementExpiryMs = (announcement: Announcement): number | null => {
    const rawExpiry = announcement.expires_at ?? announcement.expiresAt;
    if (!rawExpiry) return null;

    const expiresAt = Date.parse(rawExpiry);
    return Number.isFinite(expiresAt) ? expiresAt : null;
};

const isAnnouncementActiveNow = (announcement: Announcement): boolean => {
    const expiresAt = getAnnouncementExpiryMs(announcement);
    return expiresAt === null || expiresAt - Date.now() > ANNOUNCEMENT_EXPIRY_BUFFER_MS;
};

export const AnnouncementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { settings: platformSettings } = usePlatformSettings();
    const { user, authToken, loading: authLoading } = useUser();
    const { pusher } = useSocket();
    const hasUsableAuth = !authLoading && Boolean(user) && isAccessTokenUsable(authToken);
    const authKey = hasUsableAuth
        ? String((user as any).id || user?.email || "authenticated")
        : "";
    const [pendingAnnouncements, setPendingAnnouncements] = useState<Announcement[]>([]);
    const [currentAnnouncement, setCurrentAnnouncement] = useState<Announcement | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [locallySeenIds, setLocallySeenIds] = useState<Set<string>>(() => readDismissedAnnouncementIds());
    const locallySeenIdsRef = useRef(locallySeenIds);
    const isModalOpenRef = useRef(isModalOpen);
    const fetchRequestIdRef = useRef(0);

    useEffect(() => {
        locallySeenIdsRef.current = locallySeenIds;
    }, [locallySeenIds]);

    useEffect(() => {
        isModalOpenRef.current = isModalOpen;
    }, [isModalOpen]);

    const fetchPendingAnnouncements = useCallback(async () => {
        if (!authKey || !platformSettings.announcementsEnabled) {
            setPendingAnnouncements([]);
            setCurrentAnnouncement(null);
            setIsModalOpen(false);
            return;
        }

        const requestId = ++fetchRequestIdRef.current;

        try {
            const response = await axiosInstance.get(API_ENDPOINTS.auth.announcements.pending);
            if (requestId !== fetchRequestIdRef.current) return;

            const rawAnnouncements = Array.isArray(response.data)
                ? response.data
                : response.data?.announcements || [];
            const nextPending = rawAnnouncements.filter(
                (announcement: Announcement) =>
                    isAnnouncementActiveNow(announcement) &&
                    !locallySeenIdsRef.current.has(String(announcement.id))
            );
            setPendingAnnouncements(nextPending);
            
            // Auto-show the first pending announcement if modal isn't already open
            if (nextPending.length > 0 && !isModalOpenRef.current) {
                setCurrentAnnouncement(nextPending[0]);
                setIsModalOpen(true);
            } else if (nextPending.length === 0) {
                setCurrentAnnouncement(null);
                setIsModalOpen(false);
            }
        } catch (error) {
            if (requestId !== fetchRequestIdRef.current) return;

            logger.error("announcement.pending.fetch_failed", error);
            setPendingAnnouncements([]);
            setCurrentAnnouncement(null);
            setIsModalOpen(false);
        }
    }, [authKey, platformSettings.announcementsEnabled]);

    const showNextAnnouncement = useCallback(() => {
        if (pendingAnnouncements.length > 0) {
            setCurrentAnnouncement(pendingAnnouncements[0]);
            setIsModalOpen(true);
        }
    }, [pendingAnnouncements]);

    const dismissAnnouncement = useCallback(async (id: string, action: "clicked_cta" | "dismissed" = "dismissed") => {
        setLocallySeenIds((prev) => {
            const nextSeenIds = new Set(prev).add(id);
            locallySeenIdsRef.current = nextSeenIds;
            writeDismissedAnnouncementIds(nextSeenIds);
            return nextSeenIds;
        });
        const nextPending = pendingAnnouncements.filter((a) => a.id !== id);
        setPendingAnnouncements(nextPending);
        const nextAnnouncement = nextPending[0] || null;
        setCurrentAnnouncement(nextAnnouncement);
        setIsModalOpen(Boolean(nextAnnouncement));

        if (!hasUsableAuth) return;

        try {
            await axiosInstance.post(API_ENDPOINTS.auth.announcements.markSeen, {
                announcement_id: id,
                action_taken: action,
            });
        } catch (error) {
            logger.error("announcement.dismiss_failed", error, { announcement_id: id });
        }
    }, [hasUsableAuth, pendingAnnouncements]);

    // Fetch announcements when user logs in
    useEffect(() => {
        if (authKey && platformSettings.announcementsEnabled) {
            fetchPendingAnnouncements();
        } else {
            fetchRequestIdRef.current += 1;
            setPendingAnnouncements([]);
            setCurrentAnnouncement(null);
            setIsModalOpen(false);
        }
    }, [authKey, fetchPendingAnnouncements, platformSettings.announcementsEnabled]);

    useEffect(() => {
        if (!currentAnnouncement) return;

        const expiresAt = getAnnouncementExpiryMs(currentAnnouncement);
        if (expiresAt === null) return;
        const remainingMs = expiresAt - Date.now();
        if (remainingMs <= ANNOUNCEMENT_EXPIRY_BUFFER_MS) {
            setCurrentAnnouncement(null);
            setIsModalOpen(false);
            setPendingAnnouncements((prev) => prev.filter((announcement) => announcement.id !== currentAnnouncement.id));
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setCurrentAnnouncement(null);
            setIsModalOpen(false);
            setPendingAnnouncements((prev) => prev.filter((announcement) => announcement.id !== currentAnnouncement.id));
        }, remainingMs - ANNOUNCEMENT_EXPIRY_BUFFER_MS);

        return () => window.clearTimeout(timeoutId);
    }, [currentAnnouncement]);

    // Set up Pusher for real-time announcements
    useEffect(() => {
        if (!platformSettings.announcementsEnabled || !hasUsableAuth || !user || !pusher) return;

        const channelName = "announcements";
        
        try {
            if (typeof pusher.subscribe !== "function") {
                logger.warn("announcement.socket.subscribe_unavailable");
                return;
            }

            const channel = pusher.subscribe(channelName);
            
            channel.bind("new-announcement", (data: any) => {
                logger.sampledDebug("announcement.socket.received", { announcement_id: data?.id });
                fetchPendingAnnouncements();
            });

            return () => {
                try { channel.unbind("new-announcement"); } catch {
                    // The connection may already be closed during app teardown.
                }
                try { pusher.unsubscribe(channelName); } catch {
                    // The connection may already be closed during app teardown.
                }
            };
        } catch (error) {
            logger.error("announcement.socket.setup_failed", error);
        }
    }, [hasUsableAuth, user, pusher, fetchPendingAnnouncements, platformSettings.announcementsEnabled]);

    const handleModalClose = (action: "clicked_cta" | "dismissed" = "dismissed") => {
        if (currentAnnouncement) {
            void dismissAnnouncement(currentAnnouncement.id, action);
            return;
        }

        setIsModalOpen(false);
    };

    return (
        <AnnouncementContext.Provider
            value={{
                pendingAnnouncements,
                fetchPendingAnnouncements,
                showNextAnnouncement,
                dismissAnnouncement,
            }}
        >
            {children}
            
            {currentAnnouncement && (
                <AnnouncementModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    announcement={currentAnnouncement}
                />
            )}
        </AnnouncementContext.Provider>
    );
};

export const useAnnouncements = (): AnnouncementContextType => {
    const context = useContext(AnnouncementContext);
    if (!context) throw new Error("useAnnouncements must be used within AnnouncementProvider");
    return context;
};
